import { useDndMonitor, useDroppable } from '@dnd-kit/core';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as PanelCollapseIcon } from '../../../../assets/panel-collapse.svg';
import usePersistedState from '../../../../hooks/usePersistedState';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectOrderedTopLevelItems } from '../../../../redux/trip/trip.selectors';
import { updateList } from '../../../../redux/trip/trip.slice';
import { Trip, TripDivider, TripList } from '../../../../redux/trip/trip.types';
import { getOrderedCards, getOrderedListsForDivider } from '../../../../redux/trip/trip.utils';
import { ItemType } from '../../../../types/DragAndDrop';
import Card from '../../../Card/Card';
import DraggableCard from '../../../Card/DraggableCard';
import ColorSwatchPicker from './ColorSwatchPicker';
import './ItinerarySidebar.scss';

interface ItinerarySidebarProps {
  trip: Trip;
  canEdit?: boolean;
  open: boolean;
  onToggle: () => void;
}

// returns true if a list has at least one card
function hasCards(list: TripList, cards: Trip['cards']): boolean {
  return Boolean(list.cardOrder?.some((cardId) => cards[cardId]));
}

// returns the lists inside a divider that have cards, in order
function getVisibleNestedLists(trip: Trip, divider: TripDivider): TripList[] {
  return getOrderedListsForDivider(trip, divider).filter((list) => hasCards(list, trip.cards));
}

type RenderedItem =
  | { type: 'list'; list: TripList }
  | { type: 'divider'; divider: TripDivider; lists: TripList[] };

function ItinerarySidebar({ trip, canEdit, open, onToggle }: ItinerarySidebarProps) {
  // --- Hooks ---
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderedTopLevelItems = useAppSelector(selectOrderedTopLevelItems);

  // --- Droppable setup — accepts itinerary EVENTs dragged back from the grid ---
  const { setNodeRef: setDroppableRef, isOver: isSidebarOver } = useDroppable({
    id: 'itinerary-sidebar',
    data: { type: 'sidebar' },
  });

  // Light up the sidebar panel whenever an EVENT is being dragged (candidate drop target)
  const [isEventDragActive, setIsEventDragActive] = useState(false);
  useDndMonitor({
    onDragStart: ({ active }) => {
      setIsEventDragActive(active.data.current?.type === ItemType.EVENT);
    },
    onDragEnd: () => setIsEventDragActive(false),
    onDragCancel: () => setIsEventDragActive(false),
  });

  // --- State & refs ---
  const [expandedDividerIds, setExpandedDividerIds] = usePersistedState<Set<string>>(
    `itinerary-dividers:${trip.id}`,
    () => new Set<string>(),
    {
      serialize: (ids) => JSON.stringify(Array.from(ids)),
      deserialize: (raw) => new Set<string>(JSON.parse(raw)),
    }
  );

  const toggleDivider = useCallback(
    (id: string) => {
      setExpandedDividerIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [setExpandedDividerIds]
  );

  const [contentVisible, setContentVisible] = useState(open);
  const panelRef = useRef<HTMLDivElement>(null);
  const [colorPickerState, setColorPickerState] = useState<{
    anchorEl: HTMLElement;
    listId: string;
  } | null>(null);

  // --- Derived values ---
  const expanded = searchParams.get('list') || false;

  const handleColorBtnClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, listId: string) => {
      e.stopPropagation();
      setColorPickerState({ anchorEl: e.currentTarget, listId });
    },
    []
  );

  const handleColorSelect = useCallback(
    (hex: string | null) => {
      if (!colorPickerState) {
        return;
      }
      dispatch(
        updateList({ tripId: trip.id, listId: colorPickerState.listId, field: 'color', value: hex })
      );
      setColorPickerState(null);
    },
    [colorPickerState, dispatch, trip.id]
  );

  const handleColorPickerClose = useCallback(() => {
    setColorPickerState(null);
  }, []);

  const handleListChange = useCallback(
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setSearchParams((prev) => {
        if (isExpanded) {
          prev.set('list', panel);
        } else {
          prev.delete('list');
        }
        return prev;
      });
    },
    [setSearchParams]
  );

  const renderListAccordion = useCallback(
    (list: TripList) => {
      const isExpanded = expanded === list.id;
      const availableCards = isExpanded ? getOrderedCards(trip, list) : [];
      return (
        <Accordion
          className='itinerary-sidebar-accordion'
          key={list.id}
          expanded={isExpanded}
          onChange={handleListChange(list.id)}
          disableGutters
        >
          <AccordionSummary className='itinerary-sidebar-item-accordion-summary'>
            <div className='itinerary-sidebar-list-row'>
              <div className='itinerary-sidebar-list-title'>
                <span className='itinerary-sidebar-item-emoji'>{list.emoji}</span>{' '}
                <span>{list.name}</span>
              </div>
              {canEdit ? (
                <Tooltip
                  title='Set list color'
                  placement='top'
                  arrow
                >
                  <button
                    type='button'
                    className='itinerary-sidebar-color-btn'
                    style={{ backgroundColor: list.color ?? '#e0e0e0' }}
                    onClick={(e) => handleColorBtnClick(e, list.id)}
                    aria-label='Choose list color'
                  />
                </Tooltip>
              ) : (
                <div
                  className='itinerary-sidebar-color-btn itinerary-sidebar-color-btn--readonly'
                  style={{ backgroundColor: list.color ?? '#e0e0e0' }}
                />
              )}
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div className='itinerary-cards-container'>
              {availableCards.map((card) =>
                canEdit ? (
                  <DraggableCard
                    key={card.id}
                    card={card}
                    listId={list.id}
                    tripId={trip.id}
                    canEdit
                    listColor={list.color}
                  />
                ) : (
                  <Card
                    key={card.id}
                    card={card}
                    listId={list.id}
                    tripId={trip.id}
                    cardOrder={list.cardOrder ?? []}
                    canEdit={canEdit}
                    isItinerarySidebarCard
                    listColor={list.color}
                  />
                )
              )}
            </div>
          </AccordionDetails>
        </Accordion>
      );
    },
    [expanded, trip, canEdit, handleListChange, handleColorBtnClick]
  );

  // --- Memos ---
  // Pre-compute which items are visible — dividers with no cards are hidden entirely
  const renderedItems = useMemo((): RenderedItem[] => {
    const result: RenderedItem[] = [];
    for (const item of orderedTopLevelItems) {
      if (item.itemType === 'list') {
        if (hasCards(item.data, trip.cards)) {
          result.push({ type: 'list', list: item.data });
        }
      } else {
        const visibleLists = getVisibleNestedLists(trip, item.data);
        if (visibleLists.length > 0) {
          result.push({ type: 'divider', divider: item.data, lists: visibleLists });
        }
      }
    }
    return result;
  }, [orderedTopLevelItems, trip]);

  // --- Effects ---
  // Fade in content after sidebar width transition
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) {
      return;
    }
    let fadeTimeout: NodeJS.Timeout | null = null;
    function handleTransitionEnd(e: TransitionEvent) {
      if (e.propertyName === 'width' && open) {
        // Slightly longer delay for smoother fade-in
        fadeTimeout = setTimeout(() => setContentVisible(true), 50);
      }
      if (!open) {
        setContentVisible(false);
      }
    }
    panel.addEventListener('transitionend', handleTransitionEnd);
    return () => {
      panel.removeEventListener('transitionend', handleTransitionEnd);
      if (fadeTimeout) {
        clearTimeout(fadeTimeout);
      }
    };
  }, [open]);

  return (
    <>
      <div
        ref={setDroppableRef}
        className={[
          'itinerary-sidebar-droptarget',
          isEventDragActive ? 'event-drag-active' : '',
          isEventDragActive && isSidebarOver ? 'is-event-over' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div
          ref={panelRef}
          className={`itinerary-sidebar-panel${open ? '' : ' closed'}`}
        >
          <div className='itinerary-sidebar-panel-content'>
            {open && (
              <>
                <div className='itinerary-sidebar-header'>
                  <h4 className='itinerary-sidebar-heading'>Lists</h4>
                  <div className='itinerary-sidebar-toggle-group'>
                    <IconButton
                      size='small'
                      className='itinerary-sidebar-toggle-btn'
                      onClick={onToggle}
                      aria-label='Collapse sidebar'
                    >
                      <PanelCollapseIcon />
                    </IconButton>
                  </div>
                </div>

                <div className={`itinerary-sidebar-inner${contentVisible ? ' visible' : ''}`}>
                  <div className='itinerary-sidebar-content'>
                    {renderedItems.map((item) => {
                      if (item.type === 'list') {
                        return renderListAccordion(item.list);
                      }
                      // Divider: collapsible section header with nested list accordions
                      const isOpen = expandedDividerIds.has(item.divider.id);
                      return (
                        <React.Fragment key={item.divider.id}>
                          <div
                            className='itinerary-sidebar-divider-header'
                            onClick={() => toggleDivider(item.divider.id)}
                          >
                            <ChevronRightRoundedIcon
                              className={`itinerary-sidebar-divider-chevron${isOpen ? ' expanded' : ''}`}
                            />
                            <span>{item.divider.emoji}</span>
                            <span>{item.divider.name}</span>
                          </div>
                          <Collapse
                            in={isOpen}
                            timeout={200}
                            unmountOnExit
                          >
                            {item.lists.map((list) => renderListAccordion(list))}
                          </Collapse>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <ColorSwatchPicker
        anchorEl={colorPickerState?.anchorEl ?? null}
        currentColor={
          colorPickerState
            ? (trip.lists.find((l) => l.id === colorPickerState.listId)?.color ?? null)
            : null
        }
        onColorSelect={handleColorSelect}
        onClose={handleColorPickerClose}
      />
    </>
  );
}

ItinerarySidebar.displayName = 'ItinerarySidebar';
export default ItinerarySidebar;
